import axios from 'axios';
import { getAuthToken, removeAuthToken, removeUserInfo } from './auth';

/**
 * API 에러 타입 정의
 */
export const API_ERROR_TYPES = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  CLIENT_ERROR: 'CLIENT_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

/**
 * API 에러 정보를 담는 클래스
 */
export class APIError extends Error {
  constructor(message, type, status, response) {
    super(message);
    this.name = 'APIError';
    this.type = type;
    this.status = status;
    this.response = response;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * Axios 에러를 분석하여 APIError 객체로 변환
 * @param {Error} error - Axios 에러 객체
 * @returns {APIError} 변환된 API 에러 객체
 */
export const parseAPIError = (error) => {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      // 서버가 응답했지만 에러 상태 코드
      const status = error.response.status;
      const message = error.response.data?.message || error.response.statusText || '서버 오류가 발생했습니다.';
      
      let type = API_ERROR_TYPES.SERVER_ERROR;
      if (status >= 400 && status < 500) {
        type = API_ERROR_TYPES.CLIENT_ERROR;
      }
      
      return new APIError(message, type, status, error.response);
    } else if (error.request) {
      // 요청은 보냈지만 응답을 받지 못함
      return new APIError(
        '서버에 연결할 수 없습니다. 서버가 실행 중인지 확인해주세요.',
        API_ERROR_TYPES.NETWORK_ERROR,
        null,
        null
      );
    } else if (error.code === 'ECONNABORTED') {
      // 타임아웃 에러
      return new APIError(
        '요청 시간이 초과되었습니다. 다시 시도해주세요.',
        API_ERROR_TYPES.TIMEOUT_ERROR,
        null,
        null
      );
    }
  }
  
  // 기타 에러
  return new APIError(
    error.message || '알 수 없는 오류가 발생했습니다.',
    API_ERROR_TYPES.UNKNOWN_ERROR,
    null,
    null
  );
};

/**
 * 에러 메시지를 사용자 친화적으로 변환
 * @param {APIError} apiError - API 에러 객체
 * @returns {string} 사용자 친화적인 에러 메시지
 */
export const getUserFriendlyErrorMessage = (apiError) => {
  switch (apiError.type) {
    case API_ERROR_TYPES.NETWORK_ERROR:
      return '네트워크 연결을 확인해주세요.';
    case API_ERROR_TYPES.SERVER_ERROR:
      return `서버 오류가 발생했습니다. (${apiError.status})`;
    case API_ERROR_TYPES.CLIENT_ERROR:
      return apiError.message;
    case API_ERROR_TYPES.TIMEOUT_ERROR:
      return '요청 시간이 초과되었습니다. 다시 시도해주세요.';
    default:
      return apiError.message;
  }
};

/**
 * API 호출을 래핑하여 에러 핸들링을 자동화하는 함수
 * @param {Function} apiCall - API 호출 함수
 * @param {Object} options - 옵션 설정
 * @param {Function} options.onError - 에러 발생 시 콜백
 * @param {Function} options.onSuccess - 성공 시 콜백
 * @param {boolean} options.showSnackbar - 스낵바 표시 여부
 * @param {Function} options.showSnackbarFn - 스낵바 표시 함수
 * @returns {Promise} API 호출 결과
 */
export const withErrorHandling = async (apiCall, options = {}) => {
  const {
    onError = null,
    onSuccess = null,
    showSnackbar = false,
    showSnackbarFn = null
  } = options;

  try {
    const result = await apiCall();
    
    if (onSuccess) {
      onSuccess(result.data || result);
    }
    
    // axios response 객체의 data 속성 반환
    return { success: true, data: result.data || result };
  } catch (error) {
    const apiError = parseAPIError(error);
    const userMessage = getUserFriendlyErrorMessage(apiError);
    
    console.error('API Error:', apiError);
    
    if (onError) {
      onError(apiError, userMessage);
    }
    
    if (showSnackbar && showSnackbarFn) {
      showSnackbarFn(userMessage, 'error');
    }
    
    return { success: false, error: apiError, message: userMessage };
  }
};

/**
 * API 호출을 위한 공통 설정
 */
export const createAPIClient = (baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000') => {
  const client = axios.create({
    baseURL,
    timeout: 30000, // 30초 타임아웃
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  });

  // 요청 인터셉터
  client.interceptors.request.use(
    (config) => {
      // 인증 토큰 추가
      const token = getAuthToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
      // PUT 요청인 경우 요청 본문 로깅
      if (config.method?.toUpperCase() === 'PUT' && config.data) {
        console.log('PUT 요청 본문:', config.data);
        console.log('PUT 요청 본문 status:', config.data.status);
      }
      return config;
    },
    (error) => {
      console.error('Request Error:', error);
      return Promise.reject(error);
    }
  );

  // 응답 인터셉터
  client.interceptors.response.use(
    (response) => {
      console.log(`API Response: ${response.status} ${response.config.url}`);
      return response;
    },
    (error) => {
      console.error('Response Error:', error);
      
      // 401 에러 시 로그인 페이지로 리다이렉트
      if (error.response?.status === 401) {
        removeAuthToken();
        removeUserInfo();
        localStorage.removeItem('tokenExpiresAt');
        // 현재 주소가 로그인 페이지가 아닌 경우에만 리다이렉트
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      
      return Promise.reject(error);
    }
  );

  return client;
};

/**
 * 공통 API 클라이언트 인스턴스
 */
export const apiClient = createAPIClient();

/**
 * 에러 핸들링을 위한 React 훅
 * @param {Function} showSnackbarFn - 스낵바 표시 함수
 * @returns {Object} 에러 핸들링 함수들
 */
export const useAPIErrorHandling = (showSnackbarFn = null) => {
  const handleAPIError = (error, customMessage = null) => {
    const apiError = parseAPIError(error);
    const message = customMessage || getUserFriendlyErrorMessage(apiError);
    
    console.error('API Error:', apiError);
    
    if (showSnackbarFn) {
      showSnackbarFn(message, 'error');
    }
    
    return { error: apiError, message };
  };

  const handleAPISuccess = (message, showSnackbar = true) => {
    if (showSnackbar && showSnackbarFn) {
      showSnackbarFn(message, 'success');
    }
  };

  return {
    handleAPIError,
    handleAPISuccess,
    parseAPIError,
    getUserFriendlyErrorMessage
  };
};
